import React from 'react';
import {
  HandThumbUpIcon,
  UserCircleIcon,
  ChatBubbleLeftIcon,
  CheckCircleIcon,
  XMarkIcon,
  TrashIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { HandThumbUpIcon as HandThumbUpIconSolid, ChatBubbleLeftIcon as ChatBubbleLeftIconSolid } from '@heroicons/react/20/solid';

// Add CSS for custom scrollbar
const scrollbarStyles = `
  /* Custom scrollbar styles */
  .hide-scrollbar::-webkit-scrollbar {
    height: 4px;
    width: 4px;
  }

  .hide-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .hide-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 10px;
  }

  .hide-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }
`;

export default function NewsHomeHelp() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-gradient-to-b from-indigo-300 via-sky-100 to-white w-full">
      {/* Add custom scrollbar styles */}
      <style dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <PaperAirplaneIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">News Feed</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Comprehensive news feed system for staff communication, announcements,
            and team engagement with interactive posting and commenting features.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <PaperAirplaneIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">News Feed Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <PaperAirplaneIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Staff Posts</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Create and share news, announcements, bulletins, and events with
                  the entire organization or specific teams.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <ChatBubbleLeftIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Interactive Comments</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Engage with posts through comments, likes, and shout-outs to
                  recognize team members and foster communication.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <PaperClipIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Pinned Content</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Highlight important announcements and critical information
                  that stays visible at the top of the feed.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <UserCircleIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Team Profiles</h3>
                <p className="text-pink-700 text-sm leading-relaxed">
                  View team member profiles, online status, and activity to
                  understand who's available and active.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interface Button Layouts */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interface Button Layouts</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Header Section */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Header Navigation</h3>

              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <img src="/2.png" alt="StaffShark" className="h-8 rounded-full bg-gray-900 w-auto mr-3" />
                    <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 font-bebas">
                      News Feed
                    </h2>
                  </div>

                  <button className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                    Create Post
                  </button>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Content Filtering Tabs</h3>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-200">
                  <nav className="flex -mb-px py-1" aria-label="Tabs">
                    <button className="whitespace-nowrap py-3 px-4 font-medium border-b-2 border-indigo-500 text-indigo-600 opacity-75 cursor-not-allowed">
                      Everyone
                    </button>

                    <button className="whitespace-nowrap py-3 px-4 font-medium border-b-2 border-transparent text-gray-500 opacity-75 cursor-not-allowed">
                      Team
                    </button>

                    <button className="whitespace-nowrap py-3 px-4 font-medium border-b-2 border-transparent text-gray-500 opacity-75 cursor-not-allowed">
                      Managers
                    </button>
                  </nav>
                </div>
              </div>
            </div>

            {/* Post Creation Form */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Create Post Form</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                {/* Form Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white">Create a New Post</h2>
                  <button className="text-white opacity-75 cursor-not-allowed">
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Form Fields */}
                <div className="p-6 space-y-5">
                  {/* Title Field */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      placeholder="Enter post title..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 opacity-75 cursor-not-allowed"
                    />
                  </div>

                  {/* Content Field */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Content</label>
                    <textarea
                      rows="4"
                      placeholder="Write your post content here..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 opacity-75 cursor-not-allowed"
                    />
                  </div>

                  {/* Tags Dropdown */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Tags</label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 opacity-75 cursor-not-allowed">
                      <option>Everyone</option>
                      <option>My Team</option>
                      <option>Managers</option>
                    </select>
                  </div>

                  {/* Shout-out Selection */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Shout-out Team Members</h4>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      <button className="block w-full text-left p-2 rounded-md bg-gray-200 text-gray-900 opacity-75 cursor-not-allowed">
                        John Smith - Cashier
                      </button>
                      <button className="block w-full text-left p-2 rounded-md bg-gray-200 text-gray-900 opacity-75 cursor-not-allowed">
                        Sarah Johnson - Manager
                      </button>
                      <button className="block w-full text-left p-2 rounded-md bg-indigo-600 text-white opacity-75 cursor-not-allowed">
                        Mike Davis - Supervisor
                      </button>
                    </div>

                    <div className="mt-4">
                      <h3 className="font-semibold text-lg">Selected Shout-outs</h3>
                      <ul className="list-disc ml-6">
                        <li className="text-gray-700">Mike Davis</li>
                      </ul>
                    </div>
                  </div>

                  {/* Pinned Option */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Pin Post</label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 opacity-75 cursor-not-allowed">
                      <option>No</option>
                      <option>Yes</option>
                    </select>
                  </div>

                  {/* Post Type */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Post Type</label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 opacity-75 cursor-not-allowed">
                      <option>News</option>
                      <option>Help</option>
                      <option>Bulletin</option>
                      <option>Event</option>
                    </select>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-between gap-4">
                    <button className="flex-1 bg-blue-700 text-white py-2 rounded-md shadow-md opacity-75 cursor-not-allowed">
                      Submit Post
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Staff Profile Cards */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Team Member Profiles</h3>

              <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
                <h3 className="px-4 py-3 text-lg font-medium text-gray-900 border-b border-gray-200">
                  People
                </h3>
                <div className="flex overflow-x-auto space-x-4 p-4">
                  {/* Profile Card 1 */}
                  <button className="relative flex-shrink-0 focus:outline-none group opacity-75 cursor-not-allowed">
                    <div className="flex flex-col items-center">
                      <span className="relative inline-block">
                        <img
                          alt="John Smith's profile"
                          src="/2.png"
                          className="h-16 w-16 rounded-full object-cover border-2 border-white shadow-md"
                        />
                        <span className="absolute right-0 top-0 block h-4 w-4 rounded-full ring-2 ring-white bg-green-400" />
                      </span>
                      <span className="mt-2 text-xs font-medium text-gray-700 text-center max-w-[70px] truncate">
                        John Smith
                      </span>
                    </div>
                  </button>

                  {/* Profile Card 2 */}
                  <button className="relative flex-shrink-0 focus:outline-none group opacity-75 cursor-not-allowed">
                    <div className="flex flex-col items-center">
                      <span className="relative inline-block">
                        <img
                          alt="Sarah Johnson's profile"
                          src="/2.png"
                          className="h-16 w-16 rounded-full object-cover border-2 border-white shadow-md"
                        />
                        <span className="absolute right-0 top-0 block h-4 w-4 rounded-full ring-2 ring-white bg-red-400" />
                      </span>
                      <span className="mt-2 text-xs font-medium text-gray-700 text-center max-w-[70px] truncate">
                        Sarah Johnson
                      </span>
                    </div>
                  </button>

                  {/* Profile Card 3 */}
                  <button className="relative flex-shrink-0 focus:outline-none group opacity-75 cursor-not-allowed">
                    <div className="flex flex-col items-center">
                      <span className="relative inline-block">
                        <img
                          alt="Mike Davis's profile"
                          src="/2.png"
                          className="h-16 w-16 rounded-full object-cover border-2 border-white shadow-md"
                        />
                        <span className="absolute right-0 top-0 block h-4 w-4 rounded-full ring-2 ring-white bg-green-400" />
                      </span>
                      <span className="mt-2 text-xs font-medium text-gray-700 text-center max-w-[70px] truncate">
                        Mike Davis
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Post Card Layout */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Post Display & Interaction</h3>

              <div className="bg-white shadow rounded-lg mb-4 overflow-hidden border border-gray-200">
                {/* Post Header */}
                <div className="flex items-center p-4 border-b border-gray-100">
                  <div className="flex-shrink-0 mr-3 relative">
                    <img
                      src="/2.png"
                      alt="John Smith"
                      className="h-12 w-12 rounded-full object-cover border border-gray-200"
                    />
                    <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full ring-2 ring-white bg-green-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      John Smith <span className="ml-1 text-xs text-gray-500">• Cashier</span>
                    </p>
                    <p className="text-xs text-gray-500 truncate">October 14, 2025 at 2:30 PM</p>
                  </div>

                  <div className="ml-auto flex items-center bg-red-50 text-red-700 px-2 py-1 rounded text-xs font-medium">
                    <PaperClipIcon className="h-4 w-4 mr-1" />
                    Pinned
                  </div>
                </div>

                {/* Post Content */}
                <div className="px-4 py-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Welcome to Our New Team Member!
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-line">
                    We're excited to welcome Sarah Johnson to our team as the new Customer Service Manager.
                    Sarah brings over 5 years of experience in retail management and we're confident she'll
                    be a great addition to our team.
                  </p>

                  {/* Shout-outs */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <div className="inline-flex items-center gap-2 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium border border-indigo-100">
                      <UserCircleIcon className="h-4 w-4" />
                      <span>Sarah Johnson</span>
                    </div>
                  </div>
                </div>

                {/* Engagement Stats */}
                <div className="px-4 pb-2 flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center">
                    <span className="flex items-center">
                      <HandThumbUpIconSolid className="h-4 w-4 mr-1 text-indigo-600" />
                      12 likes
                    </span>
                  </div>
                  <div>
                    <span>8 comments</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div>
                  <div className="flex">
                    <button className="flex-1 flex items-center justify-center py-3 text-indigo-600 font-medium opacity-75 cursor-not-allowed">
                      <HandThumbUpIconSolid className="h-5 w-5 mr-2" />
                      <span>Liked</span>
                    </button>

                    <button className="flex-1 flex items-center justify-center py-3 text-indigo-600 font-medium opacity-75 cursor-not-allowed">
                      <ChatBubbleLeftIcon className="h-5 w-5 mr-2" />
                      <span>Comment</span>
                    </button>

                    <button className="flex-1 flex items-center justify-center py-3 text-indigo-600 font-medium opacity-75 cursor-not-allowed">
                      <span>Who liked</span>
                      <ChevronDownIcon className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </div>

                {/* Comments Section */}
                <div className="bg-gray-50 py-3">
                  <div className="max-h-80 overflow-y-auto px-4">
                    <ul className="space-y-3">
                      {/* Comment 1 */}
                      <li className="flex">
                        <div className="flex-shrink-0 mr-3">
                          <img
                            src="/2.png"
                            alt="Mike Davis"
                            className="h-9 w-9 rounded-full object-cover border border-gray-200"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="bg-white px-3 py-2 rounded-lg shadow-sm">
                            <p className="text-sm font-medium text-gray-900 mb-1">Mike Davis</p>
                            <p className="text-sm text-gray-700 break-words">
                              Welcome to the team Sarah! Looking forward to working with you.
                            </p>
                          </div>
                          <div className="flex items-center mt-1 ml-2 space-x-3 text-xs">
                            <span className="text-gray-500">October 14, 2025 at 2:45 PM</span>
                            <button className="text-gray-500 opacity-75 cursor-not-allowed flex items-center">
                              <TrashIcon className="h-3 w-3 mr-1" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </li>

                      {/* Comment 2 */}
                      <li className="flex">
                        <div className="flex-shrink-0 mr-3">
                          <img
                            src="/2.png"
                            alt="Emma Wilson"
                            className="h-9 w-9 rounded-full object-cover border border-gray-200"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="bg-white px-3 py-2 rounded-lg shadow-sm">
                            <p className="text-sm font-medium text-gray-900 mb-1">Emma Wilson</p>
                            <p className="text-sm text-gray-700 break-words">
                              Congratulations Sarah! The team is going to love having you here.
                            </p>
                          </div>
                          <div className="flex items-center mt-1 ml-2 space-x-3 text-xs">
                            <span className="text-gray-500">October 14, 2025 at 3:00 PM</span>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>

                  {/* Add Comment Button */}
                  <div className="px-4 pt-3 mt-2 border-t border-gray-200">
                    <button className="block w-full rounded-full border border-gray-300 bg-white py-2 px-4 text-sm text-indigo-600 opacity-75 cursor-not-allowed">
                      Add a comment...
                    </button>
                  </div>
                </div>

                {/* Likes Display */}
                <div className="bg-white p-3 rounded-b-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Liked by:</h4>
                  <div className="flex flex-wrap gap-1">
                    <div className="flex items-center bg-indigo-50 rounded-full px-3 py-1 text-xs text-indigo-700">
                      <UserCircleIcon className="h-3 w-3 mr-1" />
                      John Smith
                    </div>
                    <div className="flex items-center bg-indigo-50 rounded-full px-3 py-1 text-xs text-indigo-700">
                      <UserCircleIcon className="h-3 w-3 mr-1" />
                      Mike Davis
                    </div>
                    <div className="flex items-center bg-indigo-50 rounded-full px-3 py-1 text-xs text-indigo-700">
                      <UserCircleIcon className="h-3 w-3 mr-1" />
                      Emma Wilson
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* User Profile Sidebar */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">User Profile Sidebar</h3>

              <div className="bg-white rounded-lg shadow overflow-hidden w-full md:w-80 border border-gray-200">
                {/* User Profile Card */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4">
                  <div className="flex items-center">
                    <img
                      src="/2.png"
                      alt="John Smith"
                      className="h-12 w-12 rounded-full border-2 border-white object-cover"
                    />
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-white">John Smith</h3>
                      <p className="text-indigo-100 text-sm">Manager</p>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="px-4 py-2 border-b border-gray-200">
                  <div className="flex justify-around">
                    <div className="text-center p-2">
                      <div className="font-medium text-gray-900">24</div>
                      <div className="text-xs text-gray-500">Posts</div>
                    </div>
                    <div className="text-center p-2">
                      <div className="font-medium text-gray-900">156</div>
                      <div className="text-xs text-gray-500">Likes</div>
                    </div>
                    <div className="text-center p-2">
                      <div className="font-medium text-gray-900">89</div>
                      <div className="text-xs text-gray-500">Comments</div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="p-4">
                  <button className="w-full flex items-center justify-center bg-indigo-50 text-indigo-700 py-2 px-4 rounded-md opacity-75 cursor-not-allowed">
                    <PaperClipIcon className="h-5 w-5 mr-1.5" />
                    Create Post
                  </button>
                </div>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Post Creation</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Create posts with titles, content, tags, and shout-outs to recognize team members.
                  Choose post types and pin important announcements.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Content Filtering</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Filter posts by audience: Everyone (all staff), Team (your team only),
                  or Managers (management posts only).
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">Interactive Engagement</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Like posts, add comments, and view who liked content. Comments can be
                  added, viewed, and deleted by post authors.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-pink-900">Team Profiles</h4>
                </div>
                <p className="text-pink-700 text-sm leading-relaxed">
                  View team member profiles with online status indicators. Click profiles
                  to see individual post histories and activity.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Shout-outs</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Recognize team members by tagging them in posts. Shout-outs appear as
                  special badges and help celebrate achievements.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-yellow-900">Pinned Content</h4>
                </div>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  Important announcements stay at the top of the feed with pinned indicators.
                  Use pinning for critical information that needs visibility.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">News Feed Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Content Creation</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Use clear, descriptive titles for all posts</li>
                    <li>• Choose appropriate tags (Everyone/Team/Managers)</li>
                    <li>• Pin critical announcements and time-sensitive information</li>
                    <li>• Use shout-outs to recognize team member achievements</li>
                    <li>• Select appropriate post types (News/Help/Bulletin/Event)</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3">Engagement Guidelines</h3>
                  <ul className="space-y-2 text-sm text-indigo-700">
                    <li>• Like posts to show appreciation and support</li>
                    <li>• Add constructive comments to foster discussion</li>
                    <li>• Use the "Who liked" feature to see engagement</li>
                    <li>• Respect comment deletion rights of post authors</li>
                    <li>• Keep comments professional and team-focused</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">Team Communication</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Check online status before direct communication</li>
                    <li>• Use profile views to understand team member activity</li>
                    <li>• Filter content appropriately for different audiences</li>
                    <li>• Monitor feed regularly for important updates</li>
                    <li>• Use comments for clarification and follow-up questions</li>
                  </ul>
                </div>

                <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                  <h3 className="text-lg font-bold text-pink-900 mb-3">Content Management</h3>
                  <ul className="space-y-2 text-sm text-pink-700">
                    <li>• Review post content before publishing</li>
                    <li>• Use appropriate post types for different content</li>
                    <li>• Consider timing when posting important information</li>
                    <li>• Monitor engagement and adjust communication style</li>
                    <li>• Archive old posts when no longer relevant</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}